import classNames from "classnames";
import {
  DetailedHTMLProps,
  FocusEventHandler,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useWindowDimensions } from "../../customeHooks/useWindowDimensions";
import { Icon } from "../icon/icon";
import { ErrorText } from "../typography/errorText";

export type TextInputProps = {
  type: string;
  name: string;
  value?: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  hasIcon?: boolean;
  iconSvg?: string;
  iconClassName?: string;
  errors?: string;
  rows?: number;
  inputClass?: string;
  className?: string;
  containerClassName?: string;
  customIcon?: ReactNode;
  customIconPosition?: "left" | "right";
  hasTitle?: boolean;
  title?: string;
  autoComplete?: string;
  disabled?: boolean;
  infoElement?: ReactNode;
  readOnly?: boolean;
  autoFocus?: boolean;
  autoFocusOnMobile?: boolean;
  pattern?: string;
  customChild?: ReactNode;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const TextInput = ({
  type = "text",
  name = "",
  value = "",
  placeholder = "Placeholder",
  onChange,
  onBlur,
  onKeyDown = () => {},
  hasIcon = false,
  iconSvg = "",
  iconClassName = "",
  errors = "",
  rows = 3,
  inputClass = "",
  className = "",
  containerClassName = "",
  customIcon = undefined,
  customIconPosition = "right",
  hasTitle = false,
  title = "",
  autoComplete = "off",
  disabled = false,
  infoElement,
  readOnly = false,
  autoFocus = false,
  autoFocusOnMobile = true,
  pattern,
  customChild,
  ...inputProps
}: TextInputProps) => {
  const [viewPassword, setViewPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pholder, setPHolder] = useState<string>("");

  const inputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { width } = useWindowDimensions();

  const inputClassName = `
  bg-transparent w-full
  outline-none focus:outline-none
  font-bold
  placeholder:text-grey-700 placeholder:font-normal
  autofill:focus:bg-transparent autofill:bg-transparent
  text-sm
  transition-all duration-300
  ${inputClass}
  `;

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      if (!autoFocusOnMobile && width < 768) {
        return;
      } else {
        inputRef.current.focus();
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const textarea = textAreaRef.current;
    if (textarea) {
      // Reset the height to auto to shrink if content is deleted
      textarea.style.height = "auto";
      // Set the height to match scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  useEffect(() => {
    if ((focused && title) || !title) {
      setPHolder(placeholder);
    } else {
      setPHolder(title);
    }
  }, [focused, title, placeholder]);

  return (
    <div className={containerClassName}>
      <div
        className={classNames(
          `group w-full items-center rounded-lg border border-grey-100 bg-grey-50`,
          `px-[12px] text-sm font-normal transition-all`,
          `focus-within:border-[2px] focus-within:border-grey-950`,
          "py-[14px] md:py-[16px]",
          {
            "focus-within:!py-2": !!title,
            "!py-2": value && title,
            "focus-within:!border-danger": !!errors,
            "!bg-grey-100": disabled,
          },
          className,
        )}
        onClick={() => {
          if (name) {
            (
              document.querySelector(
                `input[name="${name}"]`,
              ) as HTMLInputElement
            )?.focus();
          }
        }}
      >
        {hasTitle && (
          <div
            className={`hidden text-xs leading-4 text-grey-700 transition-all duration-300 group-focus-within:flex ${value && "!flex"}`}
          >
            {title}
          </div>
        )}
        <div className="flex flex-row items-center gap-0">
          {hasIcon && customIconPosition !== "left" ? (
            <Icon
              svg={iconSvg}
              width={20}
              height={20}
              className={classNames("w-[24px]", iconClassName)}
            />
          ) : undefined}

          {customIcon && customIconPosition === "left" ? customIcon : undefined}

          {type !== "textarea" && !customChild ? (
            <input
              type={viewPassword ? "text" : type}
              name={name}
              value={value}
              placeholder={pholder}
              onChange={onChange}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onKeyDown={onKeyDown}
              className={inputClassName}
              autoComplete={autoComplete}
              disabled={disabled}
              readOnly={readOnly}
              pattern={pattern}
              ref={inputRef}
              {...inputProps}
            />
          ) : undefined}

          {type === "textarea" && !customChild ? (
            <textarea
              name={name}
              value={value}
              placeholder={pholder}
              onChange={onChange}
              rows={rows}
              disabled={disabled}
              className={`${inputClassName} h-auto resize-none`}
              readOnly={readOnly}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onKeyDown={onKeyDown}
              ref={textAreaRef}
            />
          ) : undefined}

          {customChild ? (
            <div
              className={`flex-1 overflow-x-scroll ${inputClassName}`}
              onBlur={(e) => {
                setFocused(false);
              }}
              onFocus={() => {
                setFocused(true);
              }}
            >
              {customChild}
            </div>
          ) : undefined}

          {type === "password" && (
            <button
              type="button"
              onClick={() => {
                setViewPassword(!viewPassword);
              }}
            >
              <Icon
                svg={viewPassword ? "hide-eye" : "show-eye"}
                width={24}
                height={24}
                className={`text-neut-700 w-[24px] cursor-pointer transition-all duration-500 ${viewPassword && "rotate-[180deg]"} `}
              />
            </button>
          )}

          {customIcon && customIconPosition === "right"
            ? customIcon
            : undefined}
        </div>
      </div>

      {errors && <ErrorText text={errors} />}

      {infoElement && (
        <div className="mt-2 text-xs !leading-5 text-primary">
          {infoElement}
        </div>
      )}
    </div>
  );
};
